import { callable } from './callable'

export type WorkspaceSavedViewId = 'all' | 'needs-attention' | 'enterprise' | 'credit-empty' | 'has-open-tickets' | 'revenue-modeled'
export type WorkspaceSort = 'lastActiveAt' | 'mrrModeled' | 'riskTier'

export interface ListOwnerWorkspacesParams {
  search?: string
  plan?: string
  type?: 'TEAM' | 'PERSONAL'
  status?: string
  preset?: WorkspaceSavedViewId
  sortBy?: WorkspaceSort
  sortDir?: 'asc' | 'desc'
  cursor?: string | null
  limit?: number
}

export interface OwnerWorkspaceListItem {
  id: string
  name: string
  shortId: string
  type: 'TEAM' | 'PERSONAL'
  plan: string
  ownerEmail: string
  status: string
  activeMemberCount: number
  helperCount: number
  storageUsedBytes: number | null
  storagePlanBytes: number | null
  creditsAvailable: number | null
  creditsSpentLifetime: number | null
  mrrModeled: number | null
  riskTier: 'Healthy' | 'Watch' | 'Critical' | null
  lastActiveAt: string | null
  /** R35: demo unlock is on for this workspace (Owner Console badge only). */
  demoUnlockEnabled?: boolean
}

export interface ListOwnerWorkspacesResult {
  workspaces: OwnerWorkspaceListItem[]
  nextCursor: string | null
  effectiveOrder?: Array<{ field: string; direction: 'asc' | 'desc' }>
  orderingChanged?: boolean
  search?: { substringCap?: number; truncated?: boolean; complete?: boolean }
}

export type OwnerWorkspaceDetailSection<T> =
  | { state: 'available'; items: T[]; nextCursor: string | null }
  | { state: 'unavailable'; message: string }

export interface OwnerWorkspaceDetail {
  ok: true
  workspace: {
    id: string; name: string; type: 'TEAM' | 'PERSONAL'; plan: string; status: string
    ownerUid: string | null; ownerEmail: string | null; owners?: Array<{ uid: string; email: string | null; displayName: string | null }>; createdAt: string | null; updatedAt: string | null
    projectionState: 'available' | 'missing' | 'unavailable'
    /** R35: per-workspace demo unlock switch (owner action SET_WORKSPACE_DEMO_UNLOCK). */
    demoUnlock?: { enabled: boolean; setBy: string | null; setAt: string | null }
    wallet: { state: 'available'; availableCredits: number | null; lifetimeSpentCredits: number | null } | { state: 'unavailable' }
    summary?: {
      activeMemberCount: number | null
      helperCount: number | null
      storageUsedBytes: number | null
      storagePlanBytes: number | null
      creditsAvailable: number | null
      creditsSpentLifetime: number | null
      mrrModeled: number | null
      estimatedCost: number | null
      marginModeled: number | null
      riskTier: string | null
      openTicketCount: number | null
      failedJobsCount: number | null
      openRiskCount: number | null
      lastActiveAt: string | null
    } | null
    moduleSignals?: {
      state: 'available' | 'unavailable'
      items: Array<{
        key: 'meetings' | 'webhooks' | 'aiChat' | 'integrations' | 'storage'
        label: string
        state: 'observed' | 'zero' | 'unknown' | 'unavailable'
        currentUsage: number | null
      }>
    }
    alerts?: Array<{
      id: string
      message: string
      severity: 'warning' | 'critical'
    }>
    /** Distinguishes assessment outcome from result content.
     *  'unavailable' = projection read failed, no evaluation possible.
     *  'unknown'     = projection document absent, cannot assess.
     *  'partial'     = projection read succeeded but a required dependency (e.g. wallet)
     *                  failed; known alerts are shown alongside incomplete assessment.
     *                  An empty alerts[] under 'partial' is NOT a genuine all-clear.
     *  'available'   = projection and all required dependencies evaluated;
     *                  empty alerts[] is a genuine all-clear.
     */
    alertsState?: 'available' | 'partial' | 'unknown' | 'unavailable'
  }
  people: OwnerWorkspaceDetailSection<{ id: string; userId: string | null; displayName?: string | null; email?: string | null; isOwner?: boolean; role: string; status: string | null; createdAt: string | null }>
  tickets: OwnerWorkspaceDetailSection<{ id: string; status: string; priority: string | null; updatedAt: string | null; createdAt: string | null }>
  failedRuns: OwnerWorkspaceDetailSection<{ id: string; workflowId: string | null; status: string; updatedAt: string | null; createdAt?: string | null }>
  audit: OwnerWorkspaceDetailSection<{ id: string; action: string; createdAt: string | null; targetId: string | null; status?: string | null }>
  activity: OwnerWorkspaceDetailSection<{ id: string; action: string; createdAt: string | null; targetId: string | null }>
}

export async function listOwnerWorkspacesV2(params: ListOwnerWorkspacesParams = {}): Promise<ListOwnerWorkspacesResult> {
  const response = await callable<ListOwnerWorkspacesParams, {
    ok: boolean
    workspaces?: OwnerWorkspaceListItem[]
    nextCursor?: string | null
    effectiveOrder?: ListOwnerWorkspacesResult['effectiveOrder']
    orderingChanged?: boolean
    search?: ListOwnerWorkspacesResult['search']
  }>('listOwnerWorkspacesV2', params)
  return {
    workspaces: response.workspaces || [],
    nextCursor: response.nextCursor || null,
    effectiveOrder: response.effectiveOrder,
    orderingChanged: response.orderingChanged,
    search: response.search,
  }
}

export async function getOwnerWorkspaceDetailV2(
  workspaceId: string,
  page?: { section?: 'people' | 'tickets' | 'failedRuns' | 'audit' | 'activity'; cursor?: string | null },
  accessReason?: unknown,
): Promise<OwnerWorkspaceDetail> {
  return callable<{ workspaceId: string; section?: string; cursor?: string | null; accessReason?: unknown }, OwnerWorkspaceDetail>(
    'getOwnerWorkspaceDetailV2',
    { workspaceId, ...page, accessReason },
  )
}

/** R35 DL2.b: a saved demo story of one workspace (server-only collection, founder-only read). */
export interface DemoStorySummary {
  storyId: string
  name: string
  createdAt: string | null
  createdBy: string | null
  totalDocs: number
  sizeBytes: number
  anchorDate: string | null
}

export async function listDemoStories(workspaceId: string, accessReason?: unknown): Promise<DemoStorySummary[]> {
  const response = await callable<{ workspaceId: string; accessReason?: unknown }, { stories?: DemoStorySummary[] }>(
    'listDemoStories',
    { workspaceId, accessReason },
  )
  return response.stories || []
}
